import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import useExporter from "../../hooks/useExporter";
import { Link, useParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import Modal from "../../components/Modal";

import { getUserProgramsByAgreementHub, deleteUserProgram } from "../../api/Agreement";
import { useAuth } from "../../providers/AuthContext";
import InputField from "../../components/InputField";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import Breadcrumbs from "../../components/Breadcrumbs";
import TableView from "../../components/TableView";

const UserProgramPage = () => {
  const { agreementPublicId } = useParams();
  const { getAccessToken } = useAuth();
  const { handleExport } = useExporter();

  const [programs, setPrograms] = useState([]);

  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filteredPrograms, setFilteredPrograms] = useState([]);

  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [programsLoaded, setProgramsLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });
  const modalRef = useRef();

  const handleOpenModal = () => {
    if (selectedPrograms.length === 0) {
      toast.error("No programs selected");
      return;
    }
    setIsModalOpen(true);
  };

  const handleExportToExcel = () => {
    const programsToExport =
      selectedPrograms.length > 0
        ? filteredPrograms.filter((program) =>
            selectedPrograms.includes(program.userProgramPublicId)
          )
        : filteredPrograms;

    if (programsToExport.length === 0) {
      toast.error("No programs selected");
      return;
    }

    const programData = programsToExport.map(
      ({
        userProgramPublicId,
        description,
        displayName,
      }) => ({
        "Program id": userProgramPublicId,
        "Description": description,
        "Name": displayName,
      })
    );

    const currentDate = new Date().toISOString().split("T")[0];
    const filename = `Users_${currentDate}`;

    handleExport({ Programs: programData }, filename);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };
    if (isFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterOpen]);

  useEffect(() => {
    handleGetProgramsForAgreement();

    const handleUserProgramUpdate = () => {
        handleGetProgramsForAgreement();
    };
    window.addEventListener("userProgramsUpdated", handleUserProgramUpdate);

    return () => {
      window.removeEventListener("userProgramsUpdated", handleUserProgramUpdate);
    };
  }, [agreementPublicId]);


  useEffect(() => {
    const applyFilterAndSort = () => {
      let filtered = programs;

      if (search) {
        const searchLower = search.toLowerCase();
        filtered = programs.filter(
          (program) =>
            program.userId?.toLowerCase().includes(searchLower) ||
          program.displayName?.toLowerCase().includes(searchLower) ||
          program.description?.toLowerCase().includes(searchLower)
        );
      }

      if (sortColumn) {
        filtered = [...filtered].sort((a, b) => {
          const aValue = (a[sortColumn] || "").toString().toLowerCase();
          const bValue = (b[sortColumn] || "").toString().toLowerCase();
          return sortOrder === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
      }

      setFilteredPrograms(filtered);
    };

    applyFilterAndSort();
  }, [search, programs, sortColumn, sortOrder]);

  const handleGetProgramsForAgreement = async () => {
    try {
      const accessToken = await getAccessToken();
      const programs = await getUserProgramsByAgreementHub(agreementPublicId, accessToken);

      setPrograms(programs);
      setFilteredPrograms(programs);
      setProgramsLoaded(true);
    } catch (error) {
      console.error(error);
      setProgramsLoaded(true);
    }
  };

  const handleDeleteUserProgram = async (userProgramPublicId) => {
    const toastId = toast.loading("Deleting user program...");
    try {
      const accessToken = await getAccessToken();
      await deleteUserProgram(userProgramPublicId, accessToken);
      const programs = await getUserProgramsByAgreementHub(accessToken);
      setPrograms(programs);

      toast.success("User program deleted", {
        id: toastId,
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });
    } catch (error) {
      toast.error("Failed to delete user program", { id: toastId });
      console.error(error);
    }
  };

  console.log("Programs:", programs);

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute w-full pointer-events-none">
              <LoadingTablePage title={"User program"} loading={!programsLoaded} />
            </div>

            <div className={`${!programsLoaded ? "loading" : ""} page-content pb-[25rem]`}>
              <Breadcrumbs />
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">User programs</h2>

                <Link
                  to={`/${agreementPublicId}/create-user-program`}
                  className="btn-primary"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2 text-lg" />
                  Create user program
                </Link>
              </div>
              <div className="rounded-md shadow-md border w-full p-4">
                <div className="flex mb-2 gap-4 justify-between">
                  <button
                    className="btn btn-outline-primary-small hidden"
                    onClick={handleOpenModal}
                    disabled={selectedPrograms.length === 0}
                  >
                    Edit
                  </button>
                  <div className="flex items-center">

                    <InputField
                      iconLeft={false}
                      icon={faSearch}
                      placeholder={"Name, Url"}
                      value={search}
                      onChange={(v) => {
                        setSearch(v.target.value);
                      }}
                    />
                    <button
                      className="btn-outline-primary-small ml-4 mt-2"
                      onClick={handleExportToExcel}
                    >
                      Export to excel
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <TableView
                    data={filteredPrograms}
                    selectedRows={selectedPrograms} // Map selectedUsers to selectedRows
                    setSelectedRows={setSelectedPrograms} // Ensure selection updates correctly
                    noCheckbox={true}
                    columns={[
                      { key: "displayName", label: "Name" },
                      { key: "description", label: "Description" },
                    ]}
                    primaryKey="userProgramPublicId" // Match this with userProgramPublicId
                    basePath={`/${agreementPublicId}/user-programs`}
                    handleDelete={handleDeleteUserProgram}
                  />
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
      <Modal isOpen={isModalOpen} setIsOpen={setIsModalOpen}>
        <div className="bg-white p-6 rounded-lg w-[400px]">
          <h2 className="text-xl font-semibold mb-4">
            Change billing group / account type
          </h2>
{/*           <div className="flex flex-col gap-4">
            <Dropdown
              label="Select billing group"
              options={billingGroups.map((group) => group.name)}
              value={
                billingGroups.find(
                  (group) => group.billingGroupPublicId === selectedBillingGroup
                )?.name || ""
              }
              onSelect={handleSelectBillingGroup}
            />

            <Dropdown
              label="Select account type"
              options={accountTypes.map((at) => at.name)}
              value={
                accountTypes.find(
                  (at) => at.accountTypePublicId === selectedAccountType
                )?.name || ""
              }
              onSelect={handleSelectAccountType}
            />
          </div>

          <div className="flex justify-end mt-4 gap-4">
            <button
              className="btn-outline-primary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
             <button className="btn-primary" onClick={handleChangeBillingGroup}>
              Confirm
            </button> 
          </div> */}
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default UserProgramPage;
